import React, { useState, useEffect } from 'react';
import LoadingSpinner from "./LoadingSpinner";
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FirebaseService } from '../services/firebaseService';

interface UpdateGroupesElectrogenesProps {
  t: any;
  isRTL: boolean;
  productId: string;
  onClose: () => void;
  onSuccess: () => void;
}

interface GroupesElectrogenesProduct {
  id?: string;
  name: string;
  mainImage: string;
  provider: string;
  description: string[];
  category: string;
  moteur?: string;
  alternateur?: string;
  capotage?: string;
  coffretDeCommande?: string;
  type?: string;
  capaciteReservoir?: string;
  autonomie75Charge?: string;
  puissancePRP?: string;
  puissanceLTP?: string;
  amperage?: string;
  regime?: string;
  frequence?: string;
  tension?: string;
  facteurPuissance?: string;
  charge25?: string;
  charge50?: string;
  charge75?: string;
  charge100?: string;
  prpPrimeRunningPower?: string;
  ltpLimitedTimePower?: string;
  // New fields
  refroidissement?: string;
  carburant?: string;
  longueur?: string;
  largeur?: string;
  hauteur?: string;
  poids?: string;
  additionalImages: string[];
}

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 1rem;
`;

const Modal = styled(motion.div)`
  background: white;
  border-radius: 20px;
  padding: 2rem;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6b7280;
  
  &:hover {
    color: #dc2626;
  }
`;

const Title = styled.h2<{ $isRTL: boolean }>`
  font-size: 1.8rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 2rem;
  text-align: center;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label<{ $isRTL: boolean }>`
  font-weight: 600;
  color: #374151;
  font-size: 0.9rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const Input = styled.input<{ $isRTL: boolean }>`
  padding: 0.75rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  &:focus {
    outline: none;
    border-color: #dc2626;
  }
`;

const Select = styled.select<{ $isRTL: boolean }>`
  padding: 0.75rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  &:focus {
    outline: none;
    border-color: #dc2626;
  }
`;

const SpecsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const SubmitButton = styled.button`
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1rem;
  font-size: 1.1rem;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const LoadingText = styled.p`
  text-align: center;
  color: #6b7280;
  padding: 2rem;
`;

const UpdateGroupesElectrogenes: React.FC<UpdateGroupesElectrogenesProps> = ({ 
  t, isRTL, productId, onClose, onSuccess 
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingProduct, setLoadingProduct] = useState(true);
  const [formData, setFormData] = useState<GroupesElectrogenesProduct>({
    name: '',
    mainImage: '',
    provider: '',
    description: [''],
    category: '1', // Groupes Electrogènes
    moteur: '',
    alternateur: '',
    capotage: '',
    coffretDeCommande: '',
    type: '',
    capaciteReservoir: '',
    autonomie75Charge: '',
    puissancePRP: '',
    puissanceLTP: '',
    amperage: '',
    regime: '',
    frequence: '',
    tension: '',
    facteurPuissance: '',
    charge25: '',
    charge50: '',
    charge75: '',
    charge100: '',
    prpPrimeRunningPower: '',
    ltpLimitedTimePower: '',
    // New fields
    refroidissement: '',
    carburant: '',
    longueur: '',
    largeur: '',
    hauteur: '',
    poids: '',
    additionalImages: []
  });

  const partners = [
    { value: 'nexans', label: 'Nexans' },
    { value: 'deutz', label: 'DEUTZ' },
    { value: 'ines', label: 'Ines Max' },
    { value: 'cahors', label: 'CAHORS' },
    { value: 'ifamel', label: 'IFAMEL' }
  ];

// eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadProduct();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const loadProduct = async () => {
    try {
      setLoadingProduct(true);
      const product = await FirebaseService.getById('products', productId) as any;
      if (product) {
        setFormData({
          id: product.id,
          name: product.name || '',
          mainImage: product.mainImage || '',
          provider: product.provider || '',
          description: product.description || [''],
          category: product.category || '1',
          moteur: product.moteur || '',
          alternateur: product.alternateur || '',
          capotage: product.capotage || '',
          coffretDeCommande: product.coffretDeCommande || '',
          type: product.type || '',
          capaciteReservoir: product.capaciteReservoir || '',
          autonomie75Charge: product.autonomie75Charge || '',
          puissancePRP: product.puissancePRP || '',
          puissanceLTP: product.puissanceLTP || '',
          amperage: product.amperage || '',
          regime: product.regime || '',
          frequence: product.frequence || '',
          tension: product.tension || '',
          facteurPuissance: product.facteurPuissance || '',
          charge25: product.charge25 || '',
          charge50: product.charge50 || '',
          charge75: product.charge75 || '',
          charge100: product.charge100 || '',
          prpPrimeRunningPower: product.prpPrimeRunningPower || '',
          ltpLimitedTimePower: product.ltpLimitedTimePower || '',
          refroidissement: product.refroidissement || '',
          carburant: product.carburant || '',
          longueur: product.longueur || '',
          largeur: product.largeur || '',
          hauteur: product.hauteur || '',
          poids: product.poids || '',
          additionalImages: product.additionalImages || []
        });
      }
    } catch (error) {
      console.error('Error loading product:', error);
    } finally {
      setLoadingProduct(false);
    }
    };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      // Filter out empty description bullets
      const filteredDescription = formData.description.filter(bullet => bullet.trim() !== '');
      
      const productData = {
        ...formData,
        description: filteredDescription
      };

      await FirebaseService.update('products', productId, productData);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error updating product:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof GroupesElectrogenesProduct, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDescriptionChange = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      description: prev.description.map((desc, i) => i === index ? value : desc)
    }));
  };

  const addDescriptionBullet = () => {
    setFormData(prev => ({
      ...prev,
      description: [...prev.description, '']
    }));
  };

  const removeDescriptionBullet = (index: number) => {
    setFormData(prev => ({
      ...prev,
      description: prev.description.filter((_, i) => i !== index)
    }));
  };


  if (loadingProduct) {
    return (
      <AnimatePresence>
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <Modal
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <LoadingSpinner />
          </Modal>
        </ModalOverlay>
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence>
      <ModalOverlay
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <Modal
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          <CloseButton onClick={onClose}>×</CloseButton>
          
          <Title $isRTL={isRTL}>Update Groupes Electrogènes</Title>
          
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label $isRTL={isRTL}>Product Name</Label>
              <Input
                $isRTL={isRTL}
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label $isRTL={isRTL}>Main Image URL</Label>
              <Input
                $isRTL={isRTL}
                type="url"
                value={formData.mainImage}
                onChange={(e) => handleInputChange('mainImage', e.target.value)}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label $isRTL={isRTL}>Provider</Label>
              <Select
                $isRTL={isRTL}
                value={formData.provider}
                onChange={(e) => handleInputChange('provider', e.target.value)}
                required
              >
                <option value="">Select Provider</option>
                {partners.map(partner => (
                  <option key={partner.value} value={partner.value}>
                    {partner.label}
                  </option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label $isRTL={isRTL}>Description</Label>
              {formData.description.map((desc, index) => (
                <div key={index} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <Input
                    $isRTL={isRTL}
                    type="text"
                    value={desc}
                    onChange={(e) => handleDescriptionChange(index, e.target.value)}
                    placeholder={`Description point ${index + 1}`}
                  />
                  {formData.description.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeDescriptionBullet(index)}
                      style={{
                        background: '#dc2626',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '0.5rem',
                        cursor: 'pointer'
                      }}
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addDescriptionBullet}
                style={{
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '0.5rem 1rem',
                  cursor: 'pointer',
                  marginTop: '0.5rem'
                }}
              >
                + Add Description Point
              </button>
            </FormGroup>

            <FormGroup>
              <Label $isRTL={isRTL}>Additional Images (comma-separated URLs)</Label>
              <Input
                $isRTL={isRTL}
                type="text"
                value={formData.additionalImages.join(', ')}
                onChange={(e) => {
                  const urls = e.target.value.split(',').map(url => url.trim()).filter(url => url);
                  setFormData(prev => ({ ...prev, additionalImages: urls }));
                }}
                placeholder="https://example.com/image1.jpg, https://example.com/image2.jpg"
              />
            </FormGroup>

            <FormGroup>
              <Label $isRTL={isRTL}>Technical Specifications</Label>
              <SpecsGrid>
                <FormGroup>
                  <Label $isRTL={isRTL}>Moteur</Label>
                  <Input
                    $isRTL={isRTL}
                    type="text"
                    value={formData.moteur || ''}
                    onChange={(e) => handleInputChange('moteur', e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <Label $isRTL={isRTL}>Alternateur</Label>
                  <Input
                    $isRTL={isRTL}
                    type="text"
                    value={formData.alternateur || ''}
                    onChange={(e) => handleInputChange('alternateur', e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <Label $isRTL={isRTL}>Capotage</Label>
                  <Input
                    $isRTL={isRTL}
                    type="text"
                    value={formData.capotage || ''}
                    onChange={(e) => handleInputChange('capotage', e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <Label $isRTL={isRTL}>Coffret de commande</Label>
                  <Input
                    $isRTL={isRTL}
                    type="text"
                    value={formData.coffretDeCommande || ''}
                    onChange={(e) => handleInputChange('coffretDeCommande', e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <Label $isRTL={isRTL}>Type</Label>
                  <Input
                    $isRTL={isRTL}
                    type="text"
                    value={formData.type || ''}
                    onChange={(e) => handleInputChange('type', e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <Label $isRTL={isRTL}>Capacité du réservoir</Label>
                  <Input
                    $isRTL={isRTL}
                    type="text"
                    value={formData.capaciteReservoir || ''}
                    onChange={(e) => handleInputChange('capaciteReservoir', e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <Label $isRTL={isRTL}>Autonomie à 75% de charge</Label>
                  <Input
                    $isRTL={isRTL}
                    type="text"
                    value={formData.autonomie75Charge || ''}
                    onChange={(e) => handleInputChange('autonomie75Charge', e.target.value)}
                  />
                </FormGroup>

<FormGroup>
  <Label $isRTL={isRTL}>Puissance (PRP)</Label>
  <Input
    $isRTL={isRTL}
    type="text"
    value={formData.puissancePRP || ''}
    onChange={(e) => handleInputChange('puissancePRP', e.target.value)}
  />
</FormGroup>

<FormGroup>
  <Label $isRTL={isRTL}>PRP (Prime Running Power)</Label>
  <Input
    $isRTL={isRTL}
    type="text"
    value={formData.prpPrimeRunningPower || ''}
    onChange={(e) => handleInputChange('prpPrimeRunningPower', e.target.value)}
  />
</FormGroup>

<FormGroup>
  <Label $isRTL={isRTL}>Puissance (LTP)</Label>
  <Input
    $isRTL={isRTL}
    type="text"
    value={formData.puissanceLTP || ''}
    onChange={(e) => handleInputChange('puissanceLTP', e.target.value)}
  />
</FormGroup>

<FormGroup>
  <Label $isRTL={isRTL}>LTP (Limited Time Power)</Label>
  <Input
    $isRTL={isRTL}
    type="text"
    value={formData.ltpLimitedTimePower || ''}
    onChange={(e) => handleInputChange('ltpLimitedTimePower', e.target.value)}
  />
</FormGroup>

<FormGroup>
  <Label $isRTL={isRTL}>Ampérage</Label>
  <Input
    $isRTL={isRTL}
    type="text"
    value={formData.amperage || ''}
    onChange={(e) => handleInputChange('amperage', e.target.value)}
  />
</FormGroup>

<FormGroup>
  <Label $isRTL={isRTL}>Régime</Label>
  <Input
    $isRTL={isRTL}
    type="text"
    value={formData.regime || ''}
    onChange={(e) => handleInputChange('regime', e.target.value)}
  />
</FormGroup>

<FormGroup>
  <Label $isRTL={isRTL}>Fréquence</Label>
  <Input
    $isRTL={isRTL}
    type="text"
    value={formData.frequence || ''}
    onChange={(e) => handleInputChange('frequence', e.target.value)}
  />
</FormGroup>

<FormGroup>
  <Label $isRTL={isRTL}>Tension</Label>
  <Input
    $isRTL={isRTL}
    type="text"
    value={formData.tension || ''}
    onChange={(e) => handleInputChange('tension', e.target.value)}
  />
</FormGroup>

<FormGroup>
  <Label $isRTL={isRTL}>Facteur de Puissance</Label>
  <Input
    $isRTL={isRTL}
    type="text"
    value={formData.facteurPuissance || ''}
    onChange={(e) => handleInputChange('facteurPuissance', e.target.value)}
  />
</FormGroup>

<FormGroup>
  <Label $isRTL={isRTL}>25% de charge</Label>
  <Input
    $isRTL={isRTL}
    type="text"
    value={formData.charge25 || ''}
    onChange={(e) => handleInputChange('charge25', e.target.value)}
  />
</FormGroup>

<FormGroup>
  <Label $isRTL={isRTL}>50% de charge</Label>
  <Input
    $isRTL={isRTL}
    type="text"
    value={formData.charge50 || ''}
    onChange={(e) => handleInputChange('charge50', e.target.value)}
  />
</FormGroup>

<FormGroup>
  <Label $isRTL={isRTL}>75% de charge</Label>
  <Input
    $isRTL={isRTL}
    type="text"
    value={formData.charge75 || ''}
    onChange={(e) => handleInputChange('charge75', e.target.value)}
  />
</FormGroup>

<FormGroup>
  <Label $isRTL={isRTL}>100% de charge</Label>
  <Input
    $isRTL={isRTL}
    type="text"
    value={formData.charge100 || ''}
    onChange={(e) => handleInputChange('charge100', e.target.value)}
  />
</FormGroup>

{/* Add Refroidissement field */}
<FormGroup>
  <Label $isRTL={isRTL}>Refroidissement</Label>
  <Input
    $isRTL={isRTL}
    type="text"
    value={formData.refroidissement || ''}
    onChange={(e) => handleInputChange('refroidissement', e.target.value)}
    placeholder="Système de refroidissement"
  />
</FormGroup>

{/* Add Carburant field */}
<FormGroup>
  <Label $isRTL={isRTL}>Carburant</Label>
  <Input
    $isRTL={isRTL}
    type="text"
    value={formData.carburant || ''}
    onChange={(e) => handleInputChange('carburant', e.target.value)}
    placeholder="Type de carburant"
  />
</FormGroup>
</SpecsGrid>
</FormGroup>

<SubmitButton type="submit" disabled={loading}>
  {loading ? 'Updating...' : 'Update Groupes Electrogènes'}
</SubmitButton>
</Form>
</Modal>
</ModalOverlay>
</AnimatePresence>

  );
};

export default UpdateGroupesElectrogenes; 